import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { boxSolid, horizontalCap, subFrame } from '../solids';
import { gableRoof } from '../roofSolids';
import { buildArchBridge } from '../../terrain/archBridge';
import { gableWall, rectWall, type MasonryStyles } from './masonry';
import { planFrameOf, meshFrameOf, toLayout } from './planFrame';
import { corners } from './genericBlock';
import { buildGasthuisCourt, pietersCourtObstacles } from './gasthuisCourt';

/** Unequal ward ranges around the court read in the museum painting. Dimensions are estimates. */
export const PIETERS_WARDS = [
  { u: 0, v: -8.45, length: 38, width: 5.9, eaves: 8.8, along: true },
  { u: -16, v: 2.925, length: 17.05, width: 6, eaves: 7.2, along: false },
  { u: 16, v: 6.75, length: 9.4, width: 6, eaves: 8, along: false },
] as const;

interface Colors {
  wall: LinearRGB;
  roof: LinearRGB;
  glass: LinearRGB;
  trim: LinearRGB;
  ground: LinearRGB;
  timber: LinearRGB;
  cloth: LinearRGB;
}

function ward(
  builder: MeshBuilder,
  frame: Frame,
  styles: MasonryStyles,
  w: (typeof PIETERS_WARDS)[number]
): void {
  const f = subFrame(frame, w.u, w.v, w.along ? 0 : Math.PI / 2);
  const r = { u0: -w.length / 2, u1: w.length / 2, v0: -w.width / 2, v1: w.width / 2 };
  const ridgeY = w.eaves + (w.width / 2) * Math.tan(0.82);
  const ring = corners(r);
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % ring.length]!;
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    const end = side % 2 === 1;
    const bays = end ? 1 : Math.max(2, Math.floor(length / 3.8));
    const openings: Ring[] = [1.35, 4.65].flatMap((y) =>
      Array.from({ length: bays }, (_, k): Ring => {
        const x = ((k + 0.5) * length) / bays;
        return [
          [x - 0.45, y],
          [x + 0.45, y],
          [x + 0.45, y + 1.7],
          [x - 0.45, y + 1.7],
        ];
      })
    );
    if (end) {
      openings.push([
        [length / 2 - 0.35, w.eaves + 0.45],
        [length / 2 + 0.35, w.eaves + 0.45],
        [length / 2 + 0.35, w.eaves + 1.3],
        [length / 2 - 0.35, w.eaves + 1.3],
      ]);
      gableWall(builder, styles, f, [from, to], { eaveY: w.eaves, apexY: ridgeY }, openings);
    } else rectWall(builder, styles, f, [from, to], [0, w.eaves], openings, 0);
    const face = subFrame(f, from[0], from[1], Math.atan2(to[1] - from[1], to[0] - from[0]));
    for (const y of [1.35, 4.65])
      for (let k = 0; k < bays; k++) {
        const x = ((k + 0.5) * length) / bays;
        boxSolid(
          builder,
          styles.glazing!.reveal,
          face,
          [x - 0.035, y, -0.045],
          [x + 0.035, y + 1.7, 0.025],
          { top: true, bottom: true }
        );
        boxSolid(
          builder,
          styles.glazing!.reveal,
          face,
          [x - 0.5, y - 0.1, -0.12],
          [x + 0.5, y + 0.03, 0.025],
          { top: true, bottom: true }
        );
      }
  });
  horizontalCap(builder, styles.wall, f, ring, 0, false);
  gableRoof(builder, styles, f, {
    ...r,
    eaveY: w.eaves,
    ridgeY,
    ends: { start: false, end: false },
  });
  boxSolid(
    builder,
    styles.wall,
    f,
    [-w.length / 4 - 0.4, ridgeY - 1, 0.3],
    [-w.length / 4 + 0.4, ridgeY + 1.1, 1.1],
    { top: true, bottom: true }
  );
}

export function pietersGasthuisObstacles(landmark: Landmark): Ring[] {
  const frame = planFrameOf(landmark.footprint, landmark.heading);
  return [
    ...PIETERS_WARDS.map((w) => {
      const hu = (w.along ? w.length : w.width) / 2;
      const hv = (w.along ? w.width : w.length) / 2;
      return corners({ u0: w.u - hu, u1: w.u + hu, v0: w.v - hv, v1: w.v + hv });
    }),
    corners({ u0: -19, u1: -1.4, v0: 10.9, v1: 11.45 }),
    corners({ u0: 1.4, u1: 19, v0: 10.9, v1: 11.45 }),
    ...pietersCourtObstacles(),
  ].map((ring) => ring.map((p) => toLayout(frame, p)));
}

/** The old Nes hospital, before the 1578 transfer into the Nonnen convents. */
export function buildSintPietersgasthuis(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: Colors,
  ids: { edgeId: number; seed: number }
): void {
  const frame = meshFrameOf(planFrameOf(landmark.footprint, landmark.heading));
  const base = { ...ids, strokeAngle: 0, extent: [38, 9] as const };
  const wall: VertexStyle = { ...base, color: colors.wall, surface: SURFACE.brick };
  const trim = { ...wall, color: colors.trim, surface: SURFACE.stone };
  const styles = {
    wall,
    roof: { ...wall, color: colors.roof, surface: SURFACE.roofSlate },
    opening: { ...wall, color: colors.glass, surface: SURFACE.linework },
    glazing: { reveal: trim },
  };
  for (const w of PIETERS_WARDS) ward(builder, frame, styles, w);
  buildArchBridge(builder, wall, subFrame(frame, -19, 11.175, 0), {
    length: 38,
    width: 0.55,
    spans: [2.8],
    pierWidth: 0.8,
    footY: -0.1,
    springY: 1.6,
    crownY: 2.75,
    deckY: () => 3.4,
    parapet: { height: 0, thickness: 0 },
  });
  horizontalCap(
    builder,
    { ...wall, color: colors.ground, surface: SURFACE.street },
    frame,
    corners({ u0: -12.9, u1: 12.9, v0: -5.4, v1: 10.85 }),
    0,
    true
  );
  buildGasthuisCourt(
    builder,
    frame,
    { ...wall, color: colors.timber, surface: SURFACE.wood },
    { ...wall, color: colors.cloth, surface: SURFACE.cloth },
    { ...wall, color: colors.ground, surface: SURFACE.cloth }
  );
}
